export type LedgerSafePrimaryRawMutation =
  | {
      action: 'put'
      databaseName: string
      storeName: string
      key: IDBValidKey
      value: unknown
    }
  | {
      action: 'delete'
      databaseName: string
      storeName: string
      key: IDBValidKey
    }

export interface LedgerSafePrimaryBridgeReceipt {
  revision: number
  duplicate: boolean
  appliedMutations: number
}

export interface LedgerSafePrimaryWriteAdapter {
  commit: (
    mutations: LedgerSafePrimaryRawMutation[],
    operationId: string,
  ) => Promise<LedgerSafePrimaryBridgeReceipt>
  cacheApplied: (receipt: LedgerSafePrimaryBridgeReceipt) => void
}

export class LedgerSafePostgresPrimaryWriteBridgeError extends Error {
  readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'LedgerSafePostgresPrimaryWriteBridgeError'
    this.code = code
  }
}

const MAX_ATOMIC_MUTATIONS = 256

let cutoverRequired = false
let activeAdapter: LedgerSafePrimaryWriteAdapter | null = null
let writeTail: Promise<void> = Promise.resolve()

const randomOperationId = (): string => {
  const uuid = globalThis.crypto?.randomUUID?.()
  return `ledger-safe-primary-write-${
    uuid || `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }`
}

export const setLedgerSafePostgresPrimaryCutoverRequired = (
  required: boolean,
): void => {
  cutoverRequired = required
}

export const installLedgerSafePostgresPrimaryWriteAdapter = (
  adapter: LedgerSafePrimaryWriteAdapter,
): (() => void) => {
  activeAdapter = adapter
  return () => {
    if (activeAdapter === adapter) {
      activeAdapter = null
    }
  }
}

export const isLedgerSafePostgresPrimaryCutoverRequired = (): boolean =>
  cutoverRequired

export const assertLedgerSafePostgresPrimaryOperationSupported = (
  operation: string,
): void => {
  if (!cutoverRequired) return
  throw new LedgerSafePostgresPrimaryWriteBridgeError(
    'primary_cutover_operation_unsupported',
    `${operation} is blocked while PostgreSQL is the source of truth because ` +
    'it has not yet been given an atomic PostgreSQL representation.',
  )
}

const executeSerialized = async <T>(
  operation: () => Promise<T>,
): Promise<T> => {
  const pending = writeTail.then(operation, operation)
  writeTail = pending.then(
    () => undefined,
    () => undefined,
  )
  return pending
}

export const runLedgerSafePostgresPrimaryWrite = async <T>({
  mutations,
  applyCache,
  operationId = randomOperationId(),
}: {
  mutations:
    | LedgerSafePrimaryRawMutation[]
    | (() => (
        LedgerSafePrimaryRawMutation[] |
        Promise<LedgerSafePrimaryRawMutation[]>
      ))
  applyCache: () => Promise<T>
  operationId?: string
}): Promise<T> => {
  if (!cutoverRequired) {
    return applyCache()
  }
  return executeSerialized(async () => {
    const adapter = activeAdapter
    if (!adapter) {
      throw new LedgerSafePostgresPrimaryWriteBridgeError(
        'primary_cutover_not_ready',
        'PostgreSQL cutover is required, but its write adapter is not ready. ' +
        'The browser cache was not changed.',
      )
    }
    const resolvedMutations = typeof mutations === 'function'
      ? await mutations()
      : mutations
    if (resolvedMutations.length === 0) {
      return applyCache()
    }
    if (resolvedMutations.length > MAX_ATOMIC_MUTATIONS) {
      throw new LedgerSafePostgresPrimaryWriteBridgeError(
        'primary_atomic_batch_too_large',
        `This operation contains ${resolvedMutations.length} record changes; ` +
        `the safe atomic limit is ${MAX_ATOMIC_MUTATIONS}. The browser cache ` +
        'was not changed.',
      )
    }
    const receipt = await adapter.commit(resolvedMutations, operationId)
    try {
      const result = await applyCache()
      adapter.cacheApplied(receipt)
      return result
    } catch (error) {
      throw new LedgerSafePostgresPrimaryWriteBridgeError(
        'primary_committed_cache_failed',
        `PostgreSQL committed revision ${receipt.revision}, but the browser ` +
        `cache update failed: ${
          error instanceof Error ? error.message : 'unknown IndexedDB error'
        }. A cache synchronization is required.`,
      )
    }
  })
}

export const indexedDbKeyFromRecord = (
  value: unknown,
  keyPath: string | readonly string[],
): IDBValidKey => {
  if (!value || typeof value !== 'object') {
    throw new LedgerSafePostgresPrimaryWriteBridgeError(
      'primary_record_key_missing',
      'A PostgreSQL-backed IndexedDB record must be an object.',
    )
  }
  const record = value as Record<string, unknown>
  const parts = (Array.isArray(keyPath) ? keyPath : [keyPath]).map(part => {
    const keyPart = record[part]
    if (
      typeof keyPart !== 'string' &&
      typeof keyPart !== 'number' &&
      !(keyPart instanceof Date) &&
      !Array.isArray(keyPart)
    ) {
      throw new LedgerSafePostgresPrimaryWriteBridgeError(
        'primary_record_key_missing',
        `The record is missing its IndexedDB key-path value ${part}.`,
      )
    }
    return keyPart as IDBValidKey
  })
  return Array.isArray(keyPath) ? parts : parts[0]
}

export const resetLedgerSafePostgresPrimaryWriteBridgeForTests = (): void => {
  cutoverRequired = false
  activeAdapter = null
  writeTail = Promise.resolve()
}
